import React, { useEffect, useState } from "react"
import styled from "styled-components"
import ButtonGroup from "./ButtonGroup"
import StackedBarChart from "../Charts/StackedBarChart"
import {
  SingleStackedBarChartData,
  StackedBarChartData,
} from "../../util/exampleData"
import LegendGroup from "./LegendGroup"
import axios from "axios"
import { CO2_FACTOR } from "../../util/Factor"

interface Props {
  location: string
  setLocation: React.Dispatch<React.SetStateAction<string>>
}

const Wrapper = styled.div`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  border-radius: 16px;
`

const Graph1 = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
`

const TitleDropDown = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;

  color: var(--base-gray100, #262626);
  font-family: SUIT;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`

const Frame = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  color: var(--base-gray100, #262626);
  font-family: SUIT;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`

const Rectangle = styled.div`
  width: 4px;
  height: 16px;
  border-radius: 99px;
  background: var(--secondary80, #4282ef);
`

const SelectWrapper = styled.div`
  display: flex;
  width: 104px;
  padding: 6px 8px;
  justify-content: space-between;
  align-items: center;

  border-radius: 8px;
  border: 1px solid var(--base-gray30, #d1d1d1);
  background: var(--base-white60, rgba(255, 255, 255, 0.6));
`

const StyledSelect = styled.select`
  color: var(--base-gray100, #262626);
  font-family: SUIT;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  border: transparent;
  background-color: transparent;

  &:focus {
    outline: none;
  }
`

const StyledOption = styled.option`
  color: #333;
  background-color: white;
  padding: 10px;
`

const GraphTable = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  align-self: stretch;
`

const GraphWrapper = styled.div`
  display: flex;
  height: 400px;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  flex: 1 0 0;
`

const TableWrapper = styled.div`
  display: flex;
  width: 200px;
  height: 400px;
  align-items: center;
  border-radius: 8px;
  border: 1px solid var(--base-gray20, #dedede);
  background: var(--base-white50, rgba(255, 255, 255, 0.5));
`

const TableColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  align-self: stretch;
`

const TableHeader = styled.div`
  display: flex;
  min-height: 40px;
  height: 46.2px;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border: 1px solid var(--base-gray10, #e8e8e8);
  background: var(--secondary10, #eef1f9);

  color: var(--base-gray100, #262626);
  text-align: center;
  font-family: SUIT;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`

const TableCell = styled.div`
  display: flex;
  height: 43.2px;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border: 1px solid var(--base-gray10, #e8e8e8);
  background: var(--base-white, #ffffff);

  color: var(--base-gray90, #404040);
  text-align: center;
  font-family: SUIT;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`

// 차트 데이터 타입
interface ChartData {
  name: string // 지역 이름
  date: string // 날짜
  value: number // 값 (사용량, 발전량, 배출량, 감축량 등)
}

// 테이블 데이터 타입
interface TableData {
  name: string // 날짜
  value: number // 값 (사용량, 발전량, 배출량, 감축량 등)
}

const CardFrame2 = (props: Props) => {
  const { location, setLocation } = props
  const [selectedButton, setSelectedButton] = useState<string>("에너지 사용량")
  const [tableColumn, setTableColumn] = useState<string>("에너지 사용량(MWh)")
  const [chartData, setChartData] = useState<ChartData[]>([]) // 차트 데이터 상태
  const [tableData, setTableData] = useState<TableData[]>([]) // 테이블 데이터 상태
  const [loading, setLoading] = useState<boolean>(true) // 로딩 상태
  const [error, setError] = useState<string | null>(null) // 에러 상태

  // 데이터 가져오기 함수
  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      // 컨트롤러 API 호출
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/smart-farm/dashboard-month-year`
      )

      const { 대전Data, 보령Data, 여수Data, 정읍Data, 전체Data, 전체합Data } =
        response.data

      // location에 따른 데이터 필터링
      const filteredData =
        location === "대전"
          ? 대전Data
          : location === "보령"
          ? 보령Data
          : location === "여수"
          ? 여수Data
          : location === "정읍"
          ? 정읍Data
          : 전체합Data // location이 "전체"인 경우

      const filteredDataForChart =
        location === "대전"
          ? 대전Data
          : location === "보령"
          ? 보령Data
          : location === "여수"
          ? 여수Data
          : location === "정읍"
          ? 정읍Data
          : 전체Data

      // 차트 데이터 변환
      const chartFormattedData = filteredDataForChart.map((item: any) => ({
        name: location === "전체" ? item.name : location, // "전체"일 경우 지역 이름 포함
        date: (() => {
          // "202401" -> "24.01"로 변환
          const year = item.curDt.slice(2, 4) // 연도
          const month = item.curDt.slice(4, 6) // 월
          return `${year}.${month}`
        })(),
        value: (() => {
          switch (selectedButton) {
            case "에너지 사용량":
              return item.curUsg // 에너지 사용량
            case "에너지 발전량":
              return item.curGen // 에너지 발전량
            case "온실가스 배출량":
              return item.curUsg * CO2_FACTOR // 소비량의 40%
            case "온실가스 감축량":
              return item.curGen * CO2_FACTOR // 발전량의 40%
            default:
              return item.curGen // 기본값: 에너지 사용량
          }
        })(),
      }))

      // 테이블 데이터 변환
      const tableFormattedData = filteredData.map((item: any) => ({
        name: (() => {
          // "202401" -> "24년 01월"로 변환
          const year = item.curDt.slice(2, 4) // 연도
          const month = item.curDt.slice(4, 6) // 월
          return `${year}년 ${month}월`
        })(),
        value: (() => {
          switch (selectedButton) {
            case "에너지 사용량":
              return Number((item.curUsg / 1000).toFixed(0))
            case "에너지 발전량":
              return Number((item.curGen / 1000).toFixed(0))
            case "온실가스 배출량":
              return Number(((item.curUsg / 1000) * CO2_FACTOR).toFixed(0))
            case "온실가스 감축량":
              return Number(((item.curGen / 1000) * CO2_FACTOR).toFixed(0))
            default:
              return Number(item.curGen.toFixed(0))
          }
        })().toLocaleString(),
      }))

      setChartData(chartFormattedData)
      setTableData(tableFormattedData)
    } catch (err: any) {
      setError(err.message || "Failed to fetch data")
    } finally {
      setLoading(false)
    }
  }

  // location이나 selectedButton 변경 시 데이터 다시 가져오기
  useEffect(() => {
    fetchData()
  }, [location, selectedButton])

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFarm = e.target.value
    setLocation(newFarm)
  }

  useEffect(() => {
    switch (selectedButton) {
      case "에너지 발전량":
        setTableColumn("에너지 발전량(MWh)")
        break

      case "온실가스 배출량":
        setTableColumn("온실가스 배출량(t-CO2eq)")
        break

      case "온실가스 감축량":
        setTableColumn("온실가스 감축량(t-CO2eq)")
        break

      default:
      case "에너지 사용량":
        setTableColumn("에너지 사용량(MWh)")
        break
    }
  }, [selectedButton])

  return (
    <Wrapper>
      <Graph1>
        <TitleDropDown>
          에너지/탄소배출 현황
          <Frame>
            <Rectangle />
            지역
            <SelectWrapper>
              <StyledSelect value={location} onChange={handleSelectChange}>
                <StyledOption value="전체">전체</StyledOption>
                <StyledOption value="정읍">정읍 실증단지</StyledOption>
                <StyledOption value="대전">대전</StyledOption>
                <StyledOption value="보령">보령</StyledOption>
                <StyledOption value="여수">여수</StyledOption>
              </StyledSelect>
            </SelectWrapper>
          </Frame>
        </TitleDropDown>
        <ButtonGroup
          selectedButton={selectedButton}
          onButtonClick={(buttonLabel: string) =>
            setSelectedButton(buttonLabel)
          }
        />
        <GraphTable>
          <GraphWrapper>
            <StackedBarChart
              width="100%"
              height="100%"
              data={chartData}
              color={["#4282EF", "#AC8CFF", "#FAA93E", "#94D474"]}
            />

            {location === "전체" && <LegendGroup />}
          </GraphWrapper>
          <TableWrapper>
            <TableColumn>
              <TableHeader>월</TableHeader>
              {loading ? (
                <TableCell>로딩 중...</TableCell>
              ) : error ? (
                <TableCell>에러: {error}</TableCell>
              ) : (
                tableData.map((row, index) => (
                  <TableCell key={index}>{row.name}</TableCell>
                ))
              )}
            </TableColumn>
            <TableColumn>
              <TableHeader>{tableColumn}</TableHeader>
              {loading ? (
                <TableCell>로딩 중...</TableCell>
              ) : error ? (
                <TableCell>에러: {error}</TableCell>
              ) : (
                tableData.map((row, index) => (
                  <TableCell key={index}>{row.value}</TableCell>
                ))
              )}
            </TableColumn>
          </TableWrapper>
        </GraphTable>
      </Graph1>
    </Wrapper>
  )
}

export default CardFrame2
